import React, { useState, useEffect } from 'react';
import CreateClaimModal from './CreateClaimModal';
import ClaimDetailModal from './ClaimDetailModal';
import { adminApi } from '../services/api';
import '../assets/ClaimsManagement.css';
import '../assets/PageHeader.css';
import '../assets/Pagination.css';

const ClaimsManagement = () => {
  const [claims, setClaims] = useState([]);
  const [claimTypes, setClaimTypes] = useState([]);
  const [statistics, setStatistics] = useState({
    pending: { count: 0, totalAmount: 0 },
    approved: { count: 0, totalAmount: 0 },
    rejected: { count: 0, totalAmount: 0 },
    cancelled: { count: 0, totalAmount: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    month: '',
    year: new Date().getFullYear().toString(),
    claimantId: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [showPeriodPaidModal, setShowPeriodPaidModal] = useState(false);
  const [periodPaidClaim, setPeriodPaidClaim] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  );

  // Track viewport size so pagination can show fewer page numbers on mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get current user info
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUser(user);
    setUserRole(user.role || '');
  }, []);

  // Fetch claims and statistics
  useEffect(() => {
    fetchClaimTypes();
    fetchClaims();
    fetchStatistics();
  }, [filters, pagination.page]);

  const fetchClaimTypes = async () => {
    try {
      const response = await adminApi.getClaimTypes();
      setClaimTypes(response.data.data);
    } catch (error) {
      console.error('Error fetching claim types:', error);
    }
  };

  const fetchClaims = async () => {
    try {
      setLoading(true);
      
      const queryParams = {
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      };

      const response = await adminApi.getClaims(queryParams);
      const data = response.data;
      
      setClaims(data.data.claims);
      setPagination(prev => ({
        ...prev,
        total: data.data.pagination.total,
        pages: data.data.pagination.pages
      }));
    } catch (error) {
      console.error('Error fetching claims:', error);
      setError('Failed to load claims');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const queryParams = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
      const response = await adminApi.getClaimsStatistics(queryParams);
      setStatistics(response.data.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleApproveClaim = async (claimId, notes = '') => {
    try {
      const response = await adminApi.approveClaim(claimId, notes);
      const claimData = response.data.data;
      
      // Check if the period is already paid (claim fully approved but can't auto-process)
      if (claimData.periodAlreadyPaid) {
        setPeriodPaidClaim(claimData);
        setShowPeriodPaidModal(true);
      }
      
      await fetchClaims();
      await fetchStatistics();
      setError('');
    } catch (error) {
      console.error('Error approving claim:', error);
      setError(error.response?.data?.message || error.message || 'Failed to approve claim');
    }
  };

  const handleProcessClaimToMonth = async (claimId, targetMonth, targetYear) => {
    try {
      await adminApi.processClaimToMonth(claimId, targetMonth, targetYear);
      setShowPeriodPaidModal(false);
      setPeriodPaidClaim(null);
      await fetchClaims();
      await fetchStatistics();
      setError('');
    } catch (error) {
      console.error('Error processing claim:', error);
      setError(error.response?.data?.message || error.message || 'Failed to process claim');
    }
  };

  const handleRejectClaim = async (claimId, reason) => {
    try {
      await adminApi.rejectClaim(claimId, reason);
      await fetchClaims();
      await fetchStatistics();
      setError('');
    } catch (error) {
      console.error('Error rejecting claim:', error);
      setError(error.response?.data?.message || error.message || 'Failed to reject claim');
    }
  };

  const handleDeleteClaim = async (claimId) => {
    if (!window.confirm('Are you sure you want to permanently delete this claim? This action cannot be undone.')) {
      return;
    }

    try {
      await adminApi.deleteClaim(claimId);
      await fetchClaims();
      await fetchStatistics();
      setError('');
    } catch (error) {
      console.error('Error deleting claim:', error);
      setError(error.response?.data?.message || error.message || 'Failed to delete claim');
    }
  };

  const handleCancelClaim = async (claimId, reason) => {
    if (!reason || reason.trim().length === 0) {
      setError('Cancellation reason is required');
      return;
    }

    try {
      await adminApi.cancelClaim(claimId, reason);
      await fetchClaims();
      await fetchStatistics();
      setError('');
    } catch (error) {
      console.error('Error cancelling claim:', error);
      setError(error.response?.data?.message || error.message || 'Failed to cancel claim');
    }
  };

  const canApproveClaim = (claim) => {
    // Only admins may approve/reject claims. Tutors can view/submit but the
    // approval decision is admin-only.
    return userRole === 'admin';
  };

  const canCancelClaim = (claim) => {
    return claim.status === 'pending' && currentUser && claim.claimant._id === currentUser.id;
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-SG', {
      timeZone: 'Asia/Singapore',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTypeLabel = (type) => {
    const claimType = claimTypes.find(ct => ct.value === type);
    return claimType ? claimType.label : type;
  };

  const renderApprovalProgress = (claim) => {
    if (claim.status !== 'pending') return null;

    const percentage = claim.approvalProgress?.percentage || 0;
    
    return (
      <div className="approval-progress">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
        <span className="progress-text">
          {claim.approvedCount}/{claim.requiredApprovals}
        </span>
      </div>
    );
  };

  const renderActionButtons = (claim) => {
    const buttons = [];

    // View button
    buttons.push(
      <button
        key="view"
        className="btn-sm btn-view"
        onClick={() => {
          setSelectedClaim(claim);
          setShowDetailModal(true);
        }}
      >
        View
      </button>
    );

    if (claim.status === 'pending') {
      // Approve button
      if (canApproveClaim(claim)) {
        buttons.push(
          <button
            key="approve"
            className="btn-sm btn-approve"
            onClick={() => {
              const notes = prompt('Approval notes (optional):');
              if (notes !== null) {
                handleApproveClaim(claim._id, notes);
              }
            }}
          >
            Approve
          </button>
        );

        // Reject button
        buttons.push(
          <button
            key="reject"
            className="btn-sm btn-reject"
            onClick={() => {
              const reason = prompt('Rejection reason (optional):');
              if (reason === null) return;
              handleRejectClaim(claim._id, reason);
            }}
          >
            Reject
          </button>
        );
      }

      // Cancel button (for claimant only)
      if (canCancelClaim(claim)) {
        buttons.push(
          <button
            key="cancel"
            className="btn-sm btn-cancel"
            onClick={() => {
              const reason = prompt('Cancellation reason (required):');
              if (reason) {
                handleCancelClaim(claim._id, reason);
              }
            }}
          >
            Cancel
          </button>
        );
      }
    }

    return <div className="action-buttons">{buttons}</div>;
  };

  const renderActionButtonsV2 = (claim) => {
    const buttons = [];

    // View button
    buttons.push(
      <button
        key="view"
        className="btn-icon-v2 btn-view"
        onClick={() => {
          setSelectedClaim(claim);
          setShowDetailModal(true);
        }}
        title="View Details"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>
    );

    if (claim.status === 'pending') {
      // Approve button with check icon
      if (canApproveClaim(claim)) {
        buttons.push(
          <button
            key="approve"
            className="btn-icon-v2 btn-approve"
            onClick={() => {
              const notes = prompt('Approval notes (optional):');
              if (notes !== null) {
                handleApproveClaim(claim._id, notes);
              }
            }}
            title="Approve Claim"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>
        );

        // Reject button with X icon
        buttons.push(
          <button
            key="reject"
            className="btn-icon-v2 btn-reject"
            onClick={() => {
              const reason = prompt('Rejection reason (optional):');
              if (reason === null) return;
              handleRejectClaim(claim._id, reason);
            }}
            title="Reject Claim"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        );
      }

      // Cancel button (for claimant only) with ban icon
      if (canCancelClaim(claim)) {
        buttons.push(
          <button
            key="cancel"
            className="btn-icon-v2 btn-cancel"
            onClick={() => {
              const reason = prompt('Cancellation reason (required):');
              if (reason) {
                handleCancelClaim(claim._id, reason);
              }
            }}
            title="Cancel Claim"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </button>
        );
      }
    }

    // Delete button for rejected/cancelled claims (admin only)
    if ((claim.status === 'rejected' || claim.status === 'cancelled') && userRole === 'admin') {
      buttons.push(
        <button
          key="delete"
          className="btn-icon-v2 btn-claim-delete"
          onClick={() => handleDeleteClaim(claim._id)}
          title="Delete Claim"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 6h18" />
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
        </button>
      );
    }

    return <>{buttons}</>;
  };

  if (loading && claims.length === 0) {
    return (
      <div className="claims-management">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading claims...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="claims-management">
      <div className="page-header-with-actions with-background">
        <div className="page-header-content">
          <h1 className="page-header-title">Claims Management</h1>
          <p className="page-header-subtitle">Submit and manage expense claims and reimbursements</p>
        </div>
        <div className="page-header-actions">
          {userRole === 'tutor' && (
            <button 
              className="btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              Submit New Claim
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ 
          background: '#f8d7da', 
          color: '#721c24', 
          padding: '10px', 
          borderRadius: '4px', 
          marginBottom: '20px' 
        }}>
          {error}
        </div>
      )}

      {/* Statistics Cards */}
      <div className="claims-stats">
        <div className="stat-card pending">
          <div className="stat-value">{statistics.pending.count}</div>
          <div className="stat-label">Pending</div>
          <div className="stat-amount">{formatAmount(statistics.pending.totalAmount)}</div>
        </div>
        <div className="stat-card approved">
          <div className="stat-value">{statistics.approved.count}</div>
          <div className="stat-label">Approved</div>
          <div className="stat-amount">{formatAmount(statistics.approved.totalAmount)}</div>
        </div>
        <div className="stat-card rejected">
          <div className="stat-value">{statistics.rejected.count}</div>
          <div className="stat-label">Rejected</div>
          <div className="stat-amount">{formatAmount(statistics.rejected.totalAmount)}</div>
        </div>
        <div className="stat-card cancelled">
          <div className="stat-value">{statistics.cancelled.count}</div>
          <div className="stat-label">Cancelled</div>
          <div className="stat-amount">{formatAmount(statistics.cancelled.totalAmount)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="claims-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>Status</label>
            <select 
              value={filters.status} 
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Type</label>
            <select 
              value={filters.type} 
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <option value="">All Types</option>
              {claimTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Month</label>
            <select 
              value={filters.month} 
              onChange={(e) => handleFilterChange('month', e.target.value)}
            >
              <option value="">All Months</option>
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2024, i).toLocaleDateString('en-SG', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Year</label>
            <select 
              value={filters.year} 
              onChange={(e) => handleFilterChange('year', e.target.value)}
            >
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - 2 + i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </div>

      {/* Claims Table */}
      <div className="claims-table-container-v2">
        {claims.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"></path>
                <rect x="9" y="3" width="6" height="4" rx="1"></rect>
                <line x1="9" y1="12" x2="15" y2="12"></line>
                <line x1="9" y1="16" x2="15" y2="16"></line>
              </svg>
            </div>
            <h3>No Claims Found</h3>
            <p>No claims match your current filters.</p>
            {userRole === 'tutor' && (
              <button 
                className="btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                Submit Your First Claim
              </button>
            )}
          </div>
        ) : (
          <div className="table-scroll-v2">
            <table className="claims-table-v2">
              <thead>
                <tr>
                  <th>Submitted</th>
                  <th>Claimant</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Transaction Date</th>
                  <th>Period</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {claims.map(claim => (
                  <tr key={claim._id}>
                    <td>
                      <div className="date-cell-v2">
                        <div className="date-v2">{new Date(claim.createdAt).toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore', day: '2-digit', month: 'short', year: 'numeric' })}</div>
                        <div className="time-v2">{new Date(claim.createdAt).toLocaleTimeString('en-SG', { timeZone: 'Asia/Singapore', hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                      </div>
                    </td>
                    <td>
                      <div className="claimant-cell-v2">
                        <div className="claimant-name-v2">{claim.claimant.name}</div>
                        <div className="claimant-email-v2">{claim.claimant.email}</div>
                      </div>
                    </td>
                    <td>
                      <div className="description-cell-v2">
                        <div className="item-name-v2">{claim.itemName || 'N/A'}</div>
                        <div className="claim-type-v2">{getTypeLabel(claim.type)}</div>
                      </div>
                    </td>
                    <td>
                      <div className="amount-cell-v2">{formatAmount(claim.amount)}</div>
                    </td>
                    <td>
                      <div className="transaction-date-cell-v2">
                        {claim.purchaseDate ? (
                          <>
                            <div className="date-v2">{new Date(claim.purchaseDate).toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore', day: '2-digit', month: 'short', year: 'numeric' })}</div>
                            <div className="time-v2">{new Date(claim.purchaseDate).toLocaleTimeString('en-SG', { timeZone: 'Asia/Singapore', hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                          </>
                        ) : (
                          <div className="period-only-v2">{claim.month}/{claim.year}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="period-cell-v2">
                        {new Date(claim.year, claim.month - 1).toLocaleDateString('en-SG', { month: 'short', year: 'numeric' })}
                      </div>
                    </td>
                    <td>
                      <div className="status-cell-v2">
                        <span className={`status-badge-v2 status-${claim.status}`}>
                          {claim.displayStatus || claim.status}
                        </span>
                        {claim.status === 'pending' && renderApprovalProgress(claim)}
                        {claim.status === 'approved' && claim.processedToPaymentAdjustment && (
                          <span className="invoice-month-tag">
                            → {new Date((claim.processedToYear || claim.year), (claim.processedToMonth || claim.month) - 1).toLocaleDateString('en-SG', { month: 'short', year: 'numeric' })}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="actions-cell-v2">
                        {renderActionButtonsV2(claim)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            className="pagination-nav"
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            aria-label="Previous page"
          >
            {isMobile ? '‹' : 'Previous'}
          </button>
          
          {(() => {
            const visibleCount = Math.min(isMobile ? 3 : 5, pagination.pages);
            const start = Math.min(
              Math.max(1, pagination.page - Math.floor(visibleCount / 2)),
              Math.max(1, pagination.pages - visibleCount + 1)
            );
            return Array.from({ length: visibleCount }, (_, i) => {
              const page = start + i;
              if (page > pagination.pages) return null;
              return (
                <button
                  key={page}
                  className={pagination.page === page ? 'active' : ''}
                  onClick={() => setPagination(prev => ({ ...prev, page }))}
                >
                  {page}
                </button>
              );
            });
          })()}
          
          <button
            className="pagination-nav"
            disabled={pagination.page === pagination.pages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            aria-label="Next page"
          >
            {isMobile ? '›' : 'Next'}
          </button>
          
          <span className="pagination-info">
            Page {pagination.page} of {pagination.pages} ({pagination.total} total)
          </span>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateClaimModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchClaims();
            fetchStatistics();
          }}
        />
      )}

      {showDetailModal && selectedClaim && (
        <ClaimDetailModal
          claim={selectedClaim}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedClaim(null);
          }}
          onUpdate={() => {
            fetchClaims();
            fetchStatistics();
          }}
        />
      )}

      {showPeriodPaidModal && periodPaidClaim && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setShowPeriodPaidModal(false); setPeriodPaidClaim(null); } }}>
          <div className="period-paid-modal">
            <div className="period-paid-header">
              <h3>Invoice Already Paid</h3>
              <button className="modal-close-btn" onClick={() => { setShowPeriodPaidModal(false); setPeriodPaidClaim(null); }}>×</button>
            </div>
            <div className="period-paid-body">
              <p className="period-paid-info">
                The claim by <strong>{periodPaidClaim.claimant?.name}</strong> for <strong>${periodPaidClaim.amount?.toFixed(2)}</strong> was 
                approved, but the original period ({new Date(periodPaidClaim.originalYear, periodPaidClaim.originalMonth - 1).toLocaleDateString('en-SG', { month: 'short', year: 'numeric' })}) has already been paid.
              </p>
              <p className="period-paid-question">Where would you like to apply this claim adjustment?</p>
              <div className="period-paid-actions">
                <button
                  className="btn-period-option btn-current-month"
                  onClick={() => {
                    handleProcessClaimToMonth(periodPaidClaim._id, periodPaidClaim.originalMonth, periodPaidClaim.originalYear);
                  }}
                >
                  Add to {new Date(periodPaidClaim.originalYear, periodPaidClaim.originalMonth - 1).toLocaleDateString('en-SG', { month: 'short', year: 'numeric' })} (already paid — will show as outstanding)
                </button>
                <button
                  className="btn-period-option btn-next-month"
                  onClick={() => {
                    handleProcessClaimToMonth(periodPaidClaim._id, 0, 0);
                  }}
                >
                  Add to next unpaid month
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimsManagement;